// Forwards first-touch UTM/click IDs across the cross-domain hop to the app (else
// every sign_up lands as utm_source=(none)), and detects hostile in-app webviews.

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "ttclid",
] as const;

const STORAGE_KEY = "ns_attribution";
// Same key, same {params, ts} shape, same TTL as the app's lib/attribution.ts —
// the cookie below is read on the app subdomain, so the two must agree.
const TTL_DAYS = 90;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

interface StoredTouch {
  params?: Record<string, string>;
  ts?: number;
}

// Registrable domain, so the cookie is readable on app.neosearch.neobim.ai.
// Host-only on localhost / IP literals, which browsers reject a domain for.
function cookieDomainAttribute(): string {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return "";
  const parts = host.split(".");
  if (parts.length < 2) return "";
  return `; domain=.${parts.slice(-2).join(".")}`;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      try {
        return decodeURIComponent(trimmed.slice(prefix.length));
      } catch {
        return trimmed.slice(prefix.length);
      }
    }
  }
  return null;
}

// Accepts both the current {params, ts} envelope and the bare params object the
// landing used to write, so a visitor mid-session does not lose their touch.
function parseTouch(raw: string | null): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as StoredTouch | Record<string, string> | null;
    if (!parsed || typeof parsed !== "object") return {};
    if ("params" in parsed) {
      const envelope = parsed as StoredTouch;
      if (!envelope.params || !envelope.ts) return {};
      if (Date.now() - envelope.ts > TTL_MS) return {};
      return envelope.params;
    }
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

/** Read live attribution params from the current URL. */
function liveParams(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") return out;
  const params = new URLSearchParams(window.location.search);
  for (const k of TRACKED_PARAMS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function readStored(): Record<string, string> {
  if (typeof window === "undefined") return {};
  let local: string | null = null;
  try {
    local = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // storage can throw in some in-app webviews / private mode — cookie still applies.
  }
  const fromLocal = parseTouch(local);
  if (Object.keys(fromLocal).length > 0) return fromLocal;
  return parseTouch(readCookie(STORAGE_KEY));
}

/**
 * Persist first-touch attribution. Call on app boot, before any client-side
 * navigation can strip the query string. First-touch wins so a later internal
 * navigation without params doesn't overwrite the original ad click.
 *
 * Written to a cookie scoped to the registrable domain, not just local storage:
 * storage is per-origin, so the app subdomain cannot see it. Handing the touch
 * over in the CTA's query string is not enough — in the 60 days to 2026-07-31,
 * 258 of the 699 people who reached the app from a utm-tagged landing session
 * arrived with no campaign params in the URL at all, and their eventual sign_up
 * was recorded as unattributed. Only 3 of 71 sign_up events carried a utm_source.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  const found = liveParams();
  if (Object.keys(found).length === 0) return;
  if (Object.keys(readStored()).length > 0) return;
  const json = JSON.stringify({ params: found, ts: Date.now() });
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    // storage disabled — the cookie below still carries the touch.
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${STORAGE_KEY}=${encodeURIComponent(json)}; max-age=${TTL_DAYS * 24 * 60 * 60}` +
    `; path=/; SameSite=Lax${secure}${cookieDomainAttribute()}`;
}

/** Merge live params over the stored first-touch set (live keys win, stored fill gaps). */
export function getAttributionParams(): Record<string, string> {
  return { ...readStored(), ...liveParams() };
}

/** Append forwarded attribution to a cross-domain destination URL. */
export function buildRegisterUrl(base: string): string {
  const attr = getAttributionParams();
  const keys = Object.keys(attr);
  if (!keys.length) return base;
  try {
    const url = new URL(base);
    for (const k of keys) url.searchParams.set(k, attr[k]);
    return url.toString();
  } catch {
    return base;
  }
}

/**
 * Append forwarded attribution to a same-origin path (e.g. "/demo"), so the
 * booking page carries the ad's campaign in its own URL the same way the
 * cross-domain app hop does. `new URL` needs an absolute base, so the query is
 * built by hand and the result stays relative for `href`/`navigate`.
 */
export function buildInternalUrl(path: string): string {
  const attr = getAttributionParams();
  const keys = Object.keys(attr);
  if (!keys.length) return path;
  const [beforeHash, hash = ""] = path.split("#");
  const [pathname, existingQuery = ""] = beforeHash.split("?");
  const params = new URLSearchParams(existingQuery);
  for (const k of keys) params.set(k, attr[k]);
  return `${pathname}?${params}${hash ? `#${hash}` : ""}`;
}

/**
 * Attribution in Calendly's widget shape. The iframe is a separate origin, so
 * params on our own URL never reach it — the booking record stays sourceless
 * unless they are handed over explicitly. Only defined keys are returned, so a
 * missing param never overwrites a real one with a blank.
 */
export function getCalendlyUtm(): Record<string, string> {
  const attr = getAttributionParams();
  const map: Record<string, string> = {
    utm_source: "utmSource",
    utm_medium: "utmMedium",
    utm_campaign: "utmCampaign",
    utm_content: "utmContent",
    utm_term: "utmTerm",
  };
  const out: Record<string, string> = {};
  for (const [param, calendlyKey] of Object.entries(map)) {
    if (attr[param]) out[calendlyKey] = attr[param];
  }
  return out;
}

export type InAppBrowser =
  | "instagram"
  | "facebook"
  | "tiktok"
  | "linkedin"
  | "snapchat"
  | null;

/** Detect a hostile in-app webview from the user agent. */
export function detectInAppBrowser(
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
): InAppBrowser {
  if (/instagram/i.test(ua)) return "instagram";
  if (/\bFBAN|\bFBAV|FB_IAB|FBIOS/i.test(ua)) return "facebook";
  if (/tiktok|musical_ly|BytedanceWebview/i.test(ua)) return "tiktok";
  if (/LinkedInApp/i.test(ua)) return "linkedin";
  if (/Snapchat/i.test(ua)) return "snapchat";
  return null;
}

export function isInAppBrowser(): boolean {
  return detectInAppBrowser() !== null;
}

/** True on Android, where an intent:// breakout to Chrome is possible. */
function isAndroid(): boolean {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

/**
 * Best-effort escape from an in-app webview to the system browser.
 * Android: intent:// forces Chrome. iOS: no reliable programmatic escape exists,
 * so callers should also render an instructional banner (see WebviewNotice).
 * Returns true if a breakout was attempted.
 */
export function breakoutToSystemBrowser(targetUrl: string): boolean {
  if (typeof window === "undefined") return false;
  if (isAndroid()) {
    const noScheme = targetUrl.replace(/^https?:\/\//, "");
    window.location.href =
      `intent://${noScheme}#Intent;scheme=https;package=com.android.chrome;` +
      `S.browser_fallback_url=${encodeURIComponent(targetUrl)};end`;
    return true;
  }
  return false;
}
